import {
  Box,
  Button,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import Features, { features } from '@ui/components/Features';
import { pathDict } from '@ui/config/paths';
import { COIN_MODE } from '@ui/hooks/useCoinMode';
import { useIFrameTheme } from '@ui/hooks/useIFrameTheme';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import { useIsOnVvs } from '@ui/hooks/useIsOnVvs';
import { useMaxContentWidth } from '@ui/hooks/useMaxContentWidth';
import { useTranslations } from '@ui/i18n';
import { useRouter } from '@ui/overrides/next/router';
import { PRELAUNCH } from '@ui/utils/constants';
import { formatTokenAmount } from '@ui/utils/format';
import { formatPrice } from '@ui/utils/formatters/number';
import { withNoSSR } from '@ui/utils/withNoSSR';
import { InColorMode } from '@veno-app/chakra';
import { currentWallet } from '@veno-app/wallet';
import { useEffect } from 'react';

import AddTokenButton, { TokenType } from '../AddTokenButton';
import {
  AtomDrawer,
  AtomDrawerContent,
  CroDrawer,
  CroDrawerContent,
  LatomDrawer,
  LatomDrawerContent,
  LcroDrawer,
  LcroDrawerContent,
  VnoDrawer,
  VnoDrawerContent,
} from '../GetAssetDrawers';
import Info from '../Info';
import {
  InDesktop,
  InMobile,
  RenderInMobile,
  useIsDesktop,
} from '../MobileOrDesktop';
import { useTransakModal } from '../TransakModal';
import { AccountCopyButton } from './AccountCopyButton';
import NftPanel from './NftPanel';
import useAccountModalData from './useAccountModalData';

const { disconnect } = currentWallet;

export const AccountModal = withNoSSR(() => {
  const t = useTranslations();
  const router = useRouter();
  const isDesktop = useIsDesktop();
  const isOnVvs = useIsOnVvs();
  const {
    isOpen,
    toggle,
    account,
    isConnected,
    balance,
    lcroBalance,
    atomBalance,
    latomBalance,
    vnoBalance,
    explorerUrl,
    wcroBalance,
  } = useAccountModalData();
  const maxContentWidth = useMaxContentWidth();

  const croDrawerDisclosure = useDisclosure();
  const lcroDrawerDisclosure = useDisclosure();
  const atomDrawerDisclosure = useDisclosure();
  const latomDrawerDisclosure = useDisclosure();
  const vnoDrawerDisclosure = useDisclosure();
  const drawerDisclosureList = [
    croDrawerDisclosure,
    lcroDrawerDisclosure,
    atomDrawerDisclosure,
    latomDrawerDisclosure,
    vnoDrawerDisclosure,
  ];

  const openTransakModal = useTransakModal((s) => s.onOpen);
  const { colorMode } = useIFrameTheme();

  const showDrawerContentInModal =
    isDesktop && drawerDisclosureList.some((v) => v.isOpen);

  // close all the drawers when modal is closed
  useEffect(() => {
    if (!isOpen) {
      drawerDisclosureList.forEach((disclosure) => disclosure.onClose());
    }
  });

  return (
    <>
      <InColorMode colorMode={colorMode}>
        <Modal
          size={{
            base: 'full',
            desktop: 'md',
          }}
          scrollBehavior="inside"
          onClose={toggle}
          isOpen={isOpen && isConnected}
        >
          <ModalOverlay />
          <ModalContent position="relative" w={maxContentWidth}>
            <ModalHeader textAlign="left">
              <Text
                textStyle={{
                  base: 'h3',
                  desktop: 'h4',
                }}
              >
                {t('Your wallet')}
              </Text>
            </ModalHeader>
            <ModalCloseButton
              variant={
                isDesktop ? undefined : isOnVvs ? 'vvsLight' : 'raisedActive'
              }
            />
            <ModalBody
              display="flex"
              flexDir="column"
              css={{
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                scrollbarWidth: 'none',
              }}
            >
              {!isOnVvs && <AccountCopyButton />}
              {explorerUrl && !isOnVvs ? (
                <Box hidden={showDrawerContentInModal} mt="27px">
                  <Link href={`${explorerUrl}/address/${account}`} isExternal>
                    {t('View on Cronoscan')}
                  </Link>
                </Box>
              ) : null}
              <Flex
                hidden={showDrawerContentInModal}
                direction="column"
                gap="16px"
                my="30px"
              >
                <Info
                  title="CRO"
                  link={
                    isOnVvs
                      ? undefined
                      : {
                          text: t('Get {coin}', {
                            coin: 'CRO',
                          }),
                          action: isFeatureEnabled(features.Transak)
                            ? () => {
                                toggle(false);

                                openTransakModal({
                                  onSuccessClose: () => {
                                    router.push(pathDict.cro.stake, '', {
                                      shallow: true,
                                    });
                                  },
                                });
                              }
                            : croDrawerDisclosure.onOpen,
                        }
                  }
                  value={
                    typeof balance !== 'undefined'
                      ? formatPrice(balance)
                      : undefined
                  }
                />
                <Info
                  className="veno-tour__account-modal-lcro"
                  title={
                    <Flex gap={2}>
                      LCRO <AddTokenButton tokenType={TokenType.LCRO} />
                    </Flex>
                  }
                  link={
                    isOnVvs
                      ? undefined
                      : {
                          text: t('Get {coin}', {
                            coin: 'LCRO',
                          }),
                          action: lcroDrawerDisclosure.onOpen,
                        }
                  }
                  value={
                    typeof lcroBalance !== 'undefined'
                      ? formatTokenAmount(lcroBalance) ?? ''
                      : undefined
                  }
                />
                <Features.Atom>
                  <Info
                    title={
                      <Flex gap={2}>
                        ATOM <AddTokenButton tokenType={TokenType.ATOM} />
                      </Flex>
                    }
                    link={
                      isOnVvs
                        ? undefined
                        : {
                            text: t('Get {coin}', {
                              coin: 'ATOM',
                            }),
                            action: atomDrawerDisclosure.onOpen,
                          }
                    }
                    value={
                      typeof atomBalance !== 'undefined'
                        ? formatPrice(atomBalance)
                        : undefined
                    }
                  />
                  <Info
                    title={
                      <Flex gap={2}>
                        LATOM <AddTokenButton tokenType={TokenType.LATOM} />
                      </Flex>
                    }
                    link={
                      isOnVvs
                        ? undefined
                        : {
                            text: t('Get {coin}', {
                              coin: 'LATOM',
                            }),
                            action: latomDrawerDisclosure.onOpen,
                          }
                    }
                    value={
                      typeof latomBalance !== 'undefined'
                        ? formatTokenAmount(
                            latomBalance,
                            undefined,
                            COIN_MODE.atom.liquidDecimals,
                          ) ?? ''
                        : undefined
                    }
                  />
                </Features.Atom>
                {!PRELAUNCH && (
                  <>
                    <Info
                      title={
                        <Flex gap={2}>
                          VNO <AddTokenButton tokenType={TokenType.VNO} />
                        </Flex>
                      }
                      link={
                        isOnVvs
                          ? undefined
                          : {
                              text: t('Get {coin}', {
                                coin: 'VNO',
                              }),
                              action: vnoDrawerDisclosure.onOpen,
                            }
                      }
                      value={
                        typeof vnoBalance !== 'undefined'
                          ? formatTokenAmount(vnoBalance) ?? ''
                          : undefined
                      }
                    />
                    <Info
                      title={
                        <Flex gap={2}>
                          WCRO <AddTokenButton tokenType={TokenType.WCRO} />
                        </Flex>
                      }
                      value={
                        typeof vnoBalance !== 'undefined'
                          ? formatTokenAmount(wcroBalance) ?? ''
                          : undefined
                      }
                    />
                  </>
                )}
                {isOpen && <NftPanel />}
              </Flex>
              {showDrawerContentInModal && (
                <Flex
                  flexGrow={1}
                  mt="62px"
                  minH="100%"
                  flexDir="column"
                  justify="space-between"
                >
                  {croDrawerDisclosure.isOpen && <CroDrawerContent />}
                  {vnoDrawerDisclosure.isOpen && <VnoDrawerContent />}
                  {atomDrawerDisclosure.isOpen && <AtomDrawerContent />}
                  {lcroDrawerDisclosure.isOpen && <LcroDrawerContent />}
                  {latomDrawerDisclosure.isOpen && <LatomDrawerContent />}
                </Flex>
              )}
            </ModalBody>
            <ModalFooter hidden={showDrawerContentInModal} bottom="0">
              <InMobile>
                <Button
                  variant={isOnVvs ? 'vvs' : undefined}
                  onClick={() => toggle()}
                >
                  {t('Close wallet')}
                </Button>
                <Link
                  mt="22px"
                  mb="5px"
                  variant="secondary"
                  onClick={async () => {
                    await disconnect();
                    toggle(false);
                  }}
                  textAlign="center"
                >
                  {t('Disconnect wallet')}
                </Link>
              </InMobile>
              <InDesktop>
                <Button
                  variant="secondary"
                  alignSelf="flex-start"
                  onClick={async () => {
                    await disconnect();
                    toggle(false);
                  }}
                >
                  {t('Disconnect wallet')}
                </Button>
              </InDesktop>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </InColorMode>
      <RenderInMobile>
        <CroDrawer {...croDrawerDisclosure} />
        <LcroDrawer {...lcroDrawerDisclosure} />
        <AtomDrawer {...atomDrawerDisclosure} />
        <LatomDrawer {...latomDrawerDisclosure} />
        <VnoDrawer {...vnoDrawerDisclosure} />
      </RenderInMobile>
    </>
  );
});
